import type { Observation } from "@hammer/core";
import { formatMoney } from "@hammer/core/money";
import { useMemo, useRef, useState } from "react";

const W = 640;
const H = 180;
const PAD = { l: 8, r: 64, t: 12, b: 24 };

const dtFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const dayFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

/**
 * Step chart of the current bid over time: a bid holds until the next one, so steps (not slopes)
 * are the truthful shape. One series, so no legend; the section heading names it.
 */
export function BidChart({ observations, maxBidCents, endAt }: { observations: Observation[]; maxBidCents: number | null; endAt: string | null }) {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const svg = useRef<SVGSVGElement>(null);

  const pts = useMemo(
    () => observations.filter((o) => o.currentBidCents != null).map((o) => ({ t: Date.parse(o.observedAt), until: Date.parse(o.lastSeenAt), v: o.currentBidCents!, bids: o.bidCount })),
    [observations],
  );
  if (pts.length < 2) return <p className="muted text-[14px]">The chart appears once Hammer has seen the price change.</p>;

  const t0 = pts[0]!.t;
  const lastSeen = Math.max(...pts.map((p) => p.until));
  const t1 = Math.max(lastSeen, endAt ? Math.min(Date.parse(endAt), lastSeen) : lastSeen, t0 + 60_000);
  const vMax = Math.max(...pts.map((p) => p.v), maxBidCents ?? 0) * 1.08;
  const x = (t: number) => PAD.l + ((t - t0) / (t1 - t0)) * (W - PAD.l - PAD.r);
  const y = (v: number) => H - PAD.b - (v / vMax) * (H - PAD.t - PAD.b);

  let d = `M${x(pts[0]!.t)},${y(pts[0]!.v)}`;
  for (let i = 1; i < pts.length; i++) d += `H${x(pts[i]!.t)}V${y(pts[i]!.v)}`;
  d += `H${x(t1)}`;

  const ticks = [0, 0.5, 1].map((f) => f * vMax / 1.08);
  const onMove = (e: React.PointerEvent) => {
    const r = svg.current!.getBoundingClientRect();
    const t = t0 + (((e.clientX - r.left) / r.width) * W - PAD.l) / (W - PAD.l - PAD.r) * (t1 - t0);
    let i = 0;
    while (i + 1 < pts.length && pts[i + 1]!.t <= t) i++;
    setHover(i);
  };
  const h = hover != null ? pts[hover]! : null;

  return (
    <div>
      {asTable ? (
        <table className="data">
          <thead><tr><th>Seen</th><th className="r">Bid</th><th className="r">Bids</th></tr></thead>
          <tbody>{pts.map((p) => <tr key={p.t}><td>{dtFmt.format(p.t)}</td><td className="r num">{formatMoney(p.v)}</td><td className="r num">{p.bids ?? "—"}</td></tr>)}</tbody>
        </table>
      ) : (
        <div className="relative">
          <svg ref={svg} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block touch-none" role="img" aria-label={`Bid went from ${formatMoney(pts[0]!.v)} to ${formatMoney(pts.at(-1)!.v)}`} onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
            {ticks.map((v) => (
              <g key={v}>
                <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--rule)" strokeWidth={1} />
                <text x={W - PAD.r + 6} y={y(v) + 4} fontSize={11} fill="var(--slate)" className="num">{formatMoney(Math.round(v / 100) * 100)}</text>
              </g>
            ))}
            {maxBidCents != null && (
              <g>
                <line x1={PAD.l} x2={W - PAD.r} y1={y(maxBidCents)} y2={y(maxBidCents)} stroke="var(--slate)" strokeWidth={1.5} strokeDasharray="5 4" />
                <text x={PAD.l + 2} y={y(maxBidCents) - 5} fontSize={11} fill="var(--slate)">Your max {formatMoney(maxBidCents)}</text>
              </g>
            )}
            <path d={d} fill="none" stroke="var(--series)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
            <text x={PAD.l} y={H - 6} fontSize={11} fill="var(--slate)">{dayFmt.format(t0)}</text>
            <text x={W - PAD.r} y={H - 6} fontSize={11} fill="var(--slate)" textAnchor="end">{dayFmt.format(t1)}</text>
            {h && (
              <g>
                <line x1={x(h.t)} x2={x(h.t)} y1={PAD.t} y2={H - PAD.b} stroke="var(--slate)" strokeWidth={1} />
                <circle cx={x(h.t)} cy={y(h.v)} r={5} fill="var(--series)" stroke="var(--sheet)" strokeWidth={2} />
              </g>
            )}
          </svg>
          {h && (
            <div className="absolute top-0 pointer-events-none panel !py-1.5 !px-2.5 text-[13px] shadow-sm" style={{ left: `clamp(0px, calc(${(x(h.t) / W) * 100}% - 4rem), calc(100% - 9rem))` }}>
              <div className="board text-[15px]">{formatMoney(h.v)}</div>
              <div className="muted">{dtFmt.format(h.t)}{h.bids != null ? `, ${h.bids} bids` : ""}</div>
            </div>
          )}
        </div>
      )}
      <button className="btn-quiet btn !min-h-0 !px-0 mt-1 text-[13px]" onClick={() => setAsTable(!asTable)}>{asTable ? "Show as chart" : "Show as table"}</button>
    </div>
  );
}
